import React, { useState, useMemo } from 'react';

import { Badge, Grid, IconButton, makeStyles, Menu, Divider, Button } from '@material-ui/core';
import NotificationsIcon from '@material-ui/icons/Notifications';

const useStyles = makeStyles((theme) => ({
  loadMoreButton: {
    '&:hover': {
      opacity: 0.7,
    },
    margin: 'auto',
  },
  menuContainer: {
    '& .MuiPaper-root': {
      '& ul .MuiGrid-root': {
        '&:focus': {
          outline: 'none',
        },
      },
      border: '1px solid #E7E7E7',
      borderRadius: 5,
      boxShadow: '2px 6px 10px rgba(0, 0, 0, 0.1)',
      boxSizing: 'border-box',
      maxHeight: 'calc(80vh - 100px)',
      width: 275,
    },
  },
  notificationBadge: (props) => ({
    '& span': {
      alignItems: 'center',
      backgroundColor: '#FFFFFF',
      color: '#B12A2A',
      display: 'flex',
      fontFamily: 'Roboto',
      fontSize: 11,
      fontStyle: 'normal',
      fontWeight: 'bold',
      height: 20,
      lineHeight: 13,
      textAlign: 'center',
      width: 20,
    },
    '& svg': {
      fill: props.open ? '#2AAAE2' : '#FFFFFF',
    },
  }),
  notificationItemStatus: {
    '&::after': {
      backgroundColor: '#3d8aed',
      border: '1px solid #fff',
      borderRadius: '50%',
      content: '""',
      display: 'block',
      height: 10,
      margin: '2px 0 2px 3px',
      width: 10,
    },
    '&:hover::after': {
      backgroundColor: '#fff',
      border: '2px solid #4890e2',
      height: 7,
      width: 7,
    },
    border: '2px solid #fff',
    cursor: 'pointer',
    display: 'inline-block',
    height: 15,
    marginRight: -5,
    position: 'relative',
    verticalAlign: 'middle',
    width: 15,
  },
  readAllButton: {
    '&:hover': {
      opacity: 0.7,
    },
    margin: '0 0 0 auto',
    paddingRight: 15,
  },
}));

const NotificationItem = ({ notification, isLast, readNotification }) => {
  const classes = useStyles();

  return (
    <>
      <Grid container item alignItems="center" justifyContent="space-between" wrap="nowrap" className="px-2 py-1">
        <p className="m-0" dangerouslySetInnerHTML={{ __html: notification.content }} />
        <span
          tabIndex={0}
          role="button"
          aria-label="Mark as Read"
          title="Mark as Read"
          className={classes.notificationItemStatus}
          onClick={() => readNotification(notification.id)}
          onKeyDown={() => {}}
        />
      </Grid>
      {!isLast && <Divider className="w-100" />}
    </>
  );
};

export default ({ notifications = [], totalNotifications = 0, handleLoadMoreClick, readNotification, readAllNotification }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const classes = useStyles({ open });

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const notificationsList = useMemo(
    () => (
      <Grid container item alignItems="center" justifyContent="flex-start">
        <Button color="primary" className={classes.readAllButton} onClick={readAllNotification}>
          Mark all as read
        </Button>
        {notifications.map((notification, index) => (
          <NotificationItem key={notification.id} notification={notification} readNotification={readNotification} isLast={index === notifications.length - 1} />
        ))}
        {totalNotifications > notifications.length && (
          <Button color="primary" className={classes.loadMoreButton} onClick={handleLoadMoreClick}>
            Load more
          </Button>
        )}
      </Grid>
    ),
    [notifications, totalNotifications, handleLoadMoreClick, readAllNotification, readNotification, classes.loadMoreButton, classes.readAllButton]
  );

  const menuContent = useMemo(
    () =>
      notifications.length ? (
        notificationsList
      ) : (
        <Grid container item alignItems="center" justifyContent="flex-start" className="px-2 py-1">
          No notifications
        </Grid>
      ),
    [notifications, notificationsList]
  );

  return (
    <>
      <IconButton
        aria-label={totalNotifications ? `show ${totalNotifications} new notification(s)` : 'notifications'}
        color="inherit"
        aria-controls="menu-notifications"
        aria-haspopup="true"
        onClick={handleMenu}>
        <Badge overlap="rectangular" badgeContent={totalNotifications} color="secondary" className={classes.notificationBadge}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        id="menu-notifications"
        className={classes.menuContainer}
        elevation={0}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        keepMounted
        transformOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
        open={open}
        onClose={handleClose}>
        {menuContent}
      </Menu>
    </>
  );
};
